import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@dorixona/ui'
import { env } from '../../../config/env'
import { Icon } from '../../../ui/Icon'
import type { Order } from '../../account/api'
import { isOrderClosed } from '../../account/model'
import { useOrderReview } from '../api'
import { STARS, orderTarget } from '../model'
import { ReviewSheet } from './ReviewSheet'

export interface OrderReviewProps {
  order: Order
}

/**
 * Оценка в карточке закрытого заказа: поставленная — звёздами и
 * комментарием, нет её — кнопка «Оценить заказ». Здесь покупатель пришёл
 * сам, поэтому закрытая без оценки шторка ничего не отменяет: спросить можно
 * снова. Чужих оценок покупатель не видит — их читает только админка.
 */
export function OrderReview({ order }: OrderReviewProps) {
  if (!env.features.reviews || !isOrderClosed(order.status)) return null
  return <OrderReviewBlock order={order} />
}

function OrderReviewBlock({ order }: OrderReviewProps) {
  const { t } = useTranslation('account')
  const review = useOrderReview(order.id)
  const [opened, setOpened] = useState(false)

  // Блок второстепенный: пока ответа нет, место под него не держим.
  if (review.isPending) return null

  // Оценки нет или её не удалось узнать — предлагаем оценить: если оценка
  // всё же есть, сервер ответит 409, шторка закроется, а блок перечитается.
  // Шторка живёт рядом с обоими видами: после оценки блок сменится на звёзды,
  // а «спасибо» в шторке должно остаться на месте.
  const mine = review.data
  return (
    <>
      {mine ? (
        <div className="pp-card rv-card">
          <h2>{t('review.yours')}</h2>
          <div
            className="rv-given"
            role="img"
            aria-label={t('review.stars', { count: mine.rating })}
          >
            {STARS.map((n) => (
              <Icon
                key={n}
                name="i-star"
                size={22}
                className={n <= mine.rating ? 'on' : undefined}
              />
            ))}
          </div>
          {mine.comment ? (
            <p className="rv-comment" data-selectable>
              {mine.comment}
            </p>
          ) : null}
        </div>
      ) : (
        <Button
          variant="ghost"
          className="rv-rate"
          onPress={() => {
            setOpened(true)
          }}
        >
          <Icon name="i-star" size={18} />
          {t('review.rate')}
        </Button>
      )}
      <ReviewSheet
        opened={opened}
        target={orderTarget(order)}
        onClose={() => {
          setOpened(false)
        }}
      />
    </>
  )
}
